import { useState, useEffect } from 'react';
import {
  buildFarmerFarmName,
  type ExplorerFarmerOption,
} from '../../../config/digisakaApi';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme, getModalTheme } from '../theme/themeStyles';
import {
  calculatePolygonAreaHectares,
  getPolygonInteriorLonLat,
  getPolygonCentroid,
  geoJsonGeometryToPolygonRings,
} from './DrawingUtils';
import FarmerSearchPicker from './FarmerSearchPicker';
import {
  type DigisakaLocationCodes,
  type ProvinceRow,
  type RegionRow,
  type BlockAdminSummary,
  type BlockAdminGroup,
  centroidToDropdownRows,
  fetchAdminPcodesForPoints,
  fetchBarangaysListForCityCode,
  fetchCentroidLocation,
  fetchCitiesListForProvinceCode,
  fetchProvincesListForRegionCode,
  fetchRegionsListFromApi,
  analyzeBlockAdminLocations,
  resolveLocationCodesForPayload,
} from '../utils/philippinesLocationFromCentroid';

// ── Reuse the same helpers from SaveFieldModal ──────────────────────────

const PROVINCE_CODE_MAP: Record<string, string> = {
  "Ilocos Norte": "0128", "Ilocos Sur": "0129", "La Union": "0133",
  "Pangasinan": "0155", "Batanes": "0209", "Cagayan": "0215",
  "Isabela": "0231", "Nueva Vizcaya": "0250", "Quirino": "0257",
  "Aurora": "0317", "Bataan": "0308", "Bulacan": "0314",
  "Nueva Ecija": "0349", "Pampanga": "0354", "Tarlac": "0369",
  "Zambales": "0371", "Batangas": "0410", "Cavite": "0421",
  "Laguna": "0434", "Quezon": "0456", "Rizal": "0458",
  "Marinduque": "1740", "Occidental Mindoro": "1751", "Oriental Mindoro": "1752",
  "Palawan": "1753", "Romblon": "1759", "Albay": "0505",
  "Camarines Norte": "0516", "Camarines Sur": "0517", "Catanduanes": "0520",
  "Masbate": "0541", "Sorsogon": "0562", "Aklan": "0604",
  "Antique": "0606", "Capiz": "0619", "Guimaras": "0679",
  "Iloilo": "0630", "Negros Occidental": "0645", "Bohol": "0712",
  "Cebu": "0722", "Negros Oriental": "0746", "Siquijor": "0761",
  "Biliran": "0878", "Eastern Samar": "0826", "Leyte": "0837",
  "Northern Samar": "0848", "Samar": "0860", "Southern Leyte": "0864",
  "Zamboanga del Norte": "0972", "Zamboanga del Sur": "0973", "Zamboanga Sibugay": "0983",
  "Bukidnon": "1013", "Camiguin": "1018", "Lanao del Norte": "1035",
  "Misamis Occidental": "1042", "Misamis Oriental": "1043", "Davao de Oro": "1123",
  "Davao del Norte": "1124", "Davao del Sur": "1125", "Davao Occidental": "1186",
  "Davao Oriental": "1126", "Cotabato": "1247", "Sarangani": "1280",
  "South Cotabato": "1263", "Sultan Kudarat": "1265", "Agusan del Norte": "1602",
  "Agusan del Sur": "1603", "Dinagat Islands": "1685", "Surigao del Norte": "1667",
  "Surigao del Sur": "1668", "Abra": "1401", "Apayao": "1481",
  "Benguet": "1411", "Ifugao": "1427", "Kalinga": "1432",
  "Mountain Province": "1444", "Basilan": "1507", "Lanao del Sur": "1536",
  "Maguindanao": "1538", "Sulu": "1566", "Tawi-Tawi": "1570",
  "Metro Manila": "1300",
};

function resolveProvinceCode(provinceName: string, row: ProvinceRow | undefined): string | null {
  if (row?.province_code) return row.province_code;
  const t = provinceName.trim();
  if (PROVINCE_CODE_MAP[t]) return PROVINCE_CODE_MAP[t];
  const hit = Object.keys(PROVINCE_CODE_MAP).find((k) => k.toLowerCase() === t.toLowerCase());
  return hit ? PROVINCE_CODE_MAP[hit] : null;
}

// ── Modal component ─────────────────────────────────────────────────────

interface UpdateFieldModalProps {
  field: any;
  updatedGeometry: any;
  onClose: () => void;
  onUpdate: (fieldId: string | number, payload: any) => void;
  /** Only admins / superusers may reassign the field to another farmer. */
  allowFarmerAssign?: boolean;
}

export default function UpdateFieldModal({
  field,
  updatedGeometry,
  onClose,
  onUpdate,
  allowFarmerAssign = false,
}: UpdateFieldModalProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);
  const md = getModalTheme(isDark);
  const inputClass = `${ap.input} rounded-md shadow-sm`;
  const selectClass = `${ap.select} rounded-md shadow-sm`;
  const labelClass = `block text-sm font-medium mb-1 ${ap.label}`;

  const fieldName = field?.farm_name || field?.name || '';
  const fieldId = field?.farm_id || field?.id || '?';

  // ── Location resolution state ──────────────────────────────────────────
  const [isResolvingLocation, setIsResolvingLocation] = useState(true);
  const [locationLookupError, setLocationLookupError] = useState('');
  const [resolvedCodes, setResolvedCodes] = useState<DigisakaLocationCodes | null>(null);
  const [manualLocationEdit, setManualLocationEdit] = useState(false);
  const [fullRegionsLoaded, setFullRegionsLoaded] = useState(false);
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [availableProvinces, setAvailableProvinces] = useState<ProvinceRow[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [cities, setCities] = useState<Array<{ city_code: string; city_name: string }>>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [barangays, setBarangays] = useState<Array<{ id?: number | string; barangay_name?: string }>>([]);
  const [isLoadingBarangays, setIsLoadingBarangays] = useState(false);
  const [outlierBlockNumbers, setOutlierBlockNumbers] = useState<number[]>([]);
  const [blocksCheckedCount, setBlocksCheckedCount] = useState(0);
  const [adminBlocks, setAdminBlocks] = useState<BlockAdminSummary[]>([]);
  const [adminGroups, setAdminGroups] = useState<BlockAdminGroup[]>([]);
  const [differLevelsLabel, setDifferLevelsLabel] = useState('');

  const [formValues, setFormValues] = useState({
    name: fieldName,
    region: '',
    province: '',
    municipality: '',
    barangay: '',
    total_land_area: '',
  });

  const mixedLocations = outlierBlockNumbers.length > 0 && adminGroups.length > 1;

  const existingFarmerId =
    field?.farmer_id != null && String(field.farmer_id).trim() !== '' && Number(field.farmer_id) !== 0
      ? Number(field.farmer_id)
      : null;
  const [selectedFarmer, setSelectedFarmer] = useState<ExplorerFarmerOption | null>(() =>
    existingFarmerId != null && Number.isFinite(existingFarmerId)
      ? {
          farmer_id: existingFarmerId,
          name: field?.farmer_name || field?.farmer?.name || `Farmer #${existingFarmerId}`,
          label:
            field?.farmer_label ||
            field?.farmer?.label ||
            `${field?.farmer_name || 'Farmer'} (#${existingFarmerId})`,
        }
      : null
  );

  const [isLoading, setIsLoading] = useState(false);

  // ── Autofill from WebGIS/GEE: all block centroids when multi-polygon ──
  useEffect(() => {
    const drawnField = { geometry: updatedGeometry?.geometry ?? updatedGeometry };

    const areaHa = calculatePolygonAreaHectares(drawnField);
    setFormValues((prev) => ({
      ...prev,
      name: prev.name || fieldName,
      total_land_area: areaHa > 0 ? areaHa.toFixed(2) : '0',
    }));

    const rings = geoJsonGeometryToPolygonRings(
      updatedGeometry?.geometry ?? updatedGeometry
    );
    const blockPoints = rings
      .map((ring, i) => {
        const c = getPolygonCentroid({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
        });
        if (!c) return null;
        return { id: String(i + 1), longitude: c[0], latitude: c[1] };
      })
      .filter(Boolean) as Array<{ id: string; longitude: number; latitude: number }>;

    const fallbackLonLat =
      getPolygonInteriorLonLat(drawnField) || getPolygonCentroid(drawnField);
    if (blockPoints.length === 0 && !fallbackLonLat) {
      setLocationLookupError('Could not compute a point inside the polygon.');
      setIsResolvingLocation(false);
      return;
    }

    let cancelled = false;
    setResolvedCodes(null);
    setManualLocationEdit(false);
    setFullRegionsLoaded(false);
    setOutlierBlockNumbers([]);
    setBlocksCheckedCount(0);
    setAdminBlocks([]);
    setAdminGroups([]);
    setDifferLevelsLabel('');
    setLocationLookupError('');
    setIsResolvingLocation(true);

    (async () => {
      try {
        let nameLon = fallbackLonLat?.[0];
        let nameLat = fallbackLonLat?.[1];

        if (blockPoints.length > 1) {
          const batch = await fetchAdminPcodesForPoints(blockPoints);
          if (cancelled) return;
          const analysis = analyzeBlockAdminLocations(
            batch.length
              ? batch
              : blockPoints.map((p) => ({ ...p, found: false }))
          );
          setBlocksCheckedCount(analysis.checkedCount || blockPoints.length);
          setOutlierBlockNumbers(analysis.outlierBlockNumbers);
          setAdminBlocks(analysis.blocks);
          setAdminGroups(analysis.groups);
          setDifferLevelsLabel(analysis.differLevelsLabel);

          if (analysis.foundCount === 0 && batch.length > 0) {
            setLocationLookupError('Could not resolve admin location for all blocks. Try again.');
          }

          // Enrich names for each location group (parallel).
          if (analysis.groups.length > 0) {
            const enriched = await Promise.all(
              analysis.groups.map(async (g) => {
                const pt =
                  blockPoints.find((p) => g.pointIds.includes(p.id)) ||
                  blockPoints.find((p) => p.id === String(g.blockNumbers[0]));
                if (!pt) return g;
                const loc = await fetchCentroidLocation(pt.longitude, pt.latitude);
                return {
                  ...g,
                  province: loc?.province || g.province,
                  municipality: loc?.municipality || g.municipality,
                  barangay: loc?.barangay || g.barangay,
                };
              })
            );
            if (cancelled) return;
            setAdminGroups(enriched);
            setAdminBlocks((prev) =>
              prev.map((b) => {
                const g = enriched.find((x) => x.locationKey === b.locationKey);
                return g
                  ? {
                      ...b,
                      province: g.province,
                      municipality: g.municipality,
                      barangay: g.barangay,
                    }
                  : b;
              })
            );
          }

          const majority =
            blockPoints.find((p) => p.id === analysis.majorityPointId) || blockPoints[0];
          nameLon = majority.longitude;
          nameLat = majority.latitude;
        } else if (blockPoints.length === 1) {
          setBlocksCheckedCount(1);
          nameLon = blockPoints[0].longitude;
          nameLat = blockPoints[0].latitude;
        }

        if (nameLon == null || nameLat == null) {
          setLocationLookupError('Could not compute a point inside the polygon.');
          return;
        }

        const loc = await fetchCentroidLocation(nameLon, nameLat);
        if (cancelled) return;
        if (!loc) {
          setLocationLookupError('outside_philippines');
          return;
        }

        const {
          regions: regionRows,
          provinces,
          cities: cityRows,
          barangays: brgyRows,
          codes,
        } = centroidToDropdownRows(loc);

        if (cancelled) return;
        setResolvedCodes(codes);
        setRegions(regionRows);
        setAvailableProvinces(provinces);
        setCities(cityRows);
        setBarangays(brgyRows);

        setFormValues((prev) => ({
          ...prev,
          region: loc.region,
          province: loc.province,
          municipality: loc.municipality ?? '',
          barangay: loc.barangay ?? '',
        }));
      } catch {
        if (!cancelled) {
          setLocationLookupError('autofill_failed');
        }
      } finally {
        if (!cancelled) {
          setIsResolvingLocation(false);
          setIsLoadingRegions(false);
          setIsLoadingProvinces(false);
          setIsLoadingCities(false);
          setIsLoadingBarangays(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [updatedGeometry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch barangays only after manual municipality change ─────────────
  useEffect(() => {
    if (!manualLocationEdit || isResolvingLocation || !formValues.municipality) {
      if (!formValues.municipality) {
        setBarangays([]);
        setFormValues((prev) => ({ ...prev, barangay: '' }));
      }
      return;
    }
    const selectedCity = cities.find((c) => c.city_name === formValues.municipality);
    if (!selectedCity?.city_code) return;

    let cancelled = false;

    (async () => {
      setIsLoadingBarangays(true);
      try {
        const list = await fetchBarangaysListForCityCode(selectedCity.city_code);
        if (cancelled) return;
        setBarangays(list);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setIsLoadingBarangays(false);
      }
    })();

    return () => { cancelled = true; };
  }, [formValues.municipality, cities, manualLocationEdit, isResolvingLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureFullRegionsLoaded = async () => {
    if (fullRegionsLoaded || isLoadingRegions) return;
    setIsLoadingRegions(true);
    try {
      const list = await fetchRegionsListFromApi();
      setRegions(list);
      setFullRegionsLoaded(true);
    } catch {
      /* keep centroid row */
    } finally {
      setIsLoadingRegions(false);
    }
  };

  // ── Input change handler ───────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'region') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues((prev) => ({ ...prev, region: value, province: '', municipality: '', barangay: '' }));
      setCities([]);
      setBarangays([]);
      if (!value) { setAvailableProvinces([]); return; }
      const selectedRegion = regions.find((r) => r.region_description === value);
      if (!selectedRegion?.region_code) { setAvailableProvinces([]); return; }
      setIsLoadingProvinces(true);
      void fetchProvincesListForRegionCode(selectedRegion.region_code)
        .then((list) => setAvailableProvinces(list))
        .catch(() => setAvailableProvinces([]))
        .finally(() => setIsLoadingProvinces(false));
    } else if (name === 'province') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues((prev) => ({ ...prev, province: value, municipality: '', barangay: '' }));
      setBarangays([]);
      if (!value) { setCities([]); return; }
      const selectedProvince = availableProvinces.find((p) => p.province_name.toLowerCase() === value.toLowerCase());
      const provinceCode = selectedProvince?.province_code || resolveProvinceCode(value, selectedProvince);
      if (!provinceCode) { setCities([]); return; }
      setIsLoadingCities(true);
      void fetchCitiesListForProvinceCode(provinceCode)
        .then((list) => setCities(list))
        .catch(() => setCities([]))
        .finally(() => setIsLoadingCities(false));
    } else if (name === 'municipality') {
      setManualLocationEdit(true);
      setResolvedCodes(null);
      setFormValues((prev) => ({ ...prev, municipality: value, barangay: '' }));
      setBarangays([]);
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ── Handle update ─────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (mixedLocations) {
      alert(
        `Cannot update as one field: blocks are in different ${differLevelsLabel || 'barangay or municipality'}. ` +
          'Edit the boundary so all blocks share the same location, or cancel and save them as separate fields.'
      );
      return;
    }
    if (!formValues.name.trim()) {
      alert('Field name is required.');
      return;
    }

    setIsLoading(true);
    try {
      const rawGeometry = updatedGeometry?.geometry ?? updatedGeometry;
      const id = fieldId.toString();

      const selectedRegion = regions.find((r) => r.region_description === formValues.region);
      const selectedProvince = availableProvinces.find((p) => p.province_name === formValues.province);
      const selectedCity = cities.find((c) => c.city_name === formValues.municipality) as { city_code?: string } | undefined;
      const selectedBarangay = barangays.find(
        (b) => (b.barangay_name || '').trim() === (formValues.barangay || '').trim()
      ) as { barangay_code?: string; brgy_code?: string; code?: string; psgc_code?: string; id?: string } | undefined;
      // GEE ADM P-codes for region/province/municipality/barangay (not fetch-cities).
      const locationCodes = resolveLocationCodesForPayload(
        resolvedCodes,
        selectedRegion,
        selectedProvince,
        selectedCity,
        selectedBarangay,
        formValues
      );

      // farmer_id: required for replace (create) path; org accounts may reassign.
      const payload: Record<string, unknown> = {
        name: formValues.name.trim(),
        region: locationCodes.region,
        province: locationCodes.province,
        municipality: locationCodes.municipality,
        barangay: locationCodes.barangay,
        total_land_area: formValues.total_land_area ? parseFloat(formValues.total_land_area) : 0,
      };
      const farmerId =
        (allowFarmerAssign && selectedFarmer?.farmer_id) ||
        field?.farmer_id ||
        null;
      if (farmerId != null && String(farmerId).trim() !== '' && Number(farmerId) !== 0) {
        payload.farmer_id = farmerId;
      }
      // digisaka.app PUT appends polygons; HomePage decides append vs full replace.
      const polygons = geoJsonGeometryToPolygonRings(rawGeometry);
      if (polygons.length > 0) {
        payload.polygons = polygons;
      }

      await onUpdate(id, payload);
    } catch (error: any) {
      console.error('Error in update handler:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-[9999] p-4 ${md.overlay}`}
      style={{ isolation: 'isolate' }}
    >
      <div
        className={`max-w-md w-full max-h-[90vh] flex flex-col relative ${md.dialog}`}
        style={{ zIndex: 10000, colorScheme: isDark ? 'dark' : 'light' }}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${ap.headerBorder}`}>
          <h3 className={`text-lg font-semibold ${ap.heading}`}>Update Field Boundary</h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`disabled:opacity-50 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-[min(320px,70vh)]">
          {/* Field info card */}
          <div className={`rounded-[10px] border p-3 ${md.note}`}>
            <p className={`text-xs font-medium ${ap.label}`}>
              <i className="ri-information-line mr-1"></i>
              You are updating the boundary for{' '}
              <strong>{formValues.name.trim() || fieldName || `ID: ${fieldId}`}</strong>
            </p>
          </div>

          {!isResolvingLocation && locationLookupError && (
            <div
              role="alert"
              className={`rounded-lg border px-3 py-2.5 text-xs ${
                isDark
                  ? 'border-amber-600/40 bg-amber-900/30 text-amber-200'
                  : 'border-amber-200/90 bg-amber-50 text-amber-950/90'
              }`}
            >
              <p className="font-semibold mb-0.5">
                {locationLookupError === 'outside_philippines'
                  ? 'Field appears to be outside the Philippines'
                  : 'Could not auto-detect location'}
              </p>
              <p className="leading-relaxed opacity-80">
                {locationLookupError === 'outside_philippines'
                  ? 'If you think this is a mistake, please select the region and province manually using the dropdowns below.'
                  : 'Location autofill failed. Please select the region and province manually using the dropdowns below.'}
              </p>
            </div>
          )}

          {!isResolvingLocation &&
            blocksCheckedCount > 1 &&
            !mixedLocations &&
            !locationLookupError &&
            adminGroups.length > 0 && (
            <p className={`text-[11px] ${ap.faint}`}>
              Checked {blocksCheckedCount} block centroids — same barangay/municipality.
            </p>
          )}

          {!isResolvingLocation && mixedLocations && (
            <div
              className={`rounded-[10px] border px-3 py-2.5 text-left text-xs leading-snug ${
                isDark
                  ? 'border-red-500/40 bg-red-900/25 text-red-100'
                  : 'border-red-300 bg-red-50 text-red-950'
              }`}
              role="alert"
            >
              <p className="font-semibold">Cannot update as one field</p>
              <p className="mt-1">
                Blocks are in different{' '}
                <span className="font-semibold">{differLevelsLabel || 'barangay or municipality'}</span>
                .
              </p>
              <ul className={`mt-2 space-y-1.5 ${isDark ? 'text-red-100/95' : 'text-red-950/95'}`}>
                {adminBlocks.map((b) => (
                  <li key={b.id} className="flex gap-2">
                    <span className="shrink-0 font-semibold">Block {b.blockNumber}</span>
                    <span>
                      {b.municipality}
                      {b.barangay && b.barangay !== '—' ? ` · ${b.barangay}` : ''}
                      {b.province && b.province !== '—' ? ` (${b.province})` : ''}
                      {b.isOutlier ? (
                        <span className={`ml-1 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                          — different{' '}
                          {adminGroups.find((g) => g.locationKey === b.locationKey)?.differsIn.join(' & ') ||
                            'location'}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
              <p className={`mt-2 ${isDark ? 'text-red-100/80' : 'text-red-900/80'}`}>
                Edit the boundary so all blocks share the same barangay/municipality, or cancel and
                save them as separate fields instead.
              </p>
            </div>
          )}

          {allowFarmerAssign && (
            <FarmerSearchPicker
              selected={selectedFarmer}
              onSelect={(farmer) => {
                setSelectedFarmer(farmer);
                if (farmer) {
                  setFormValues((prev) => ({
                    ...prev,
                    name: buildFarmerFarmName(farmer),
                  }));
                }
              }}
              disabled={isLoading}
            />
          )}

          {/* Field Name — auto-fills as H.Pandey Farm N when admin reassigns farmer */}
          <div>
            <label className={labelClass}>Field Name *</label>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              className={inputClass}
              placeholder={
                allowFarmerAssign
                  ? 'Select a farmer to auto-name, or type a name'
                  : 'Enter field name'
              }
              disabled={isLoading}
            />
          </div>

          {/* Total Land Area */}
          <div>
            <label className={labelClass}>Total Land Area (ha)</label>
            <input
              type="number"
              name="total_land_area"
              value={formValues.total_land_area}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className={inputClass}
              placeholder="e.g. 5.0"
              disabled={isLoading}
            />
          </div>

          {/* Administrative boundaries — loading skeleton or real selects */}
          {isResolvingLocation ? (
            <div
              className={`rounded-[10px] border p-4 ${md.locationBox}`}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${ap.muted}`}>
                Administrative boundaries
              </p>
              <p className={`mb-3 mt-1 text-xs ${ap.faint}`}>
                Checking block locations…
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(['Region', 'Province', 'Municipality', 'Barangay'] as const).map((label) => (
                  <div key={label} className="space-y-2">
                    <div className={`relative h-3 w-20 overflow-hidden rounded ${isDark ? 'bg-gray-700/90' : 'bg-gray-200/90'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-r from-transparent to-transparent animate-shimmer-slide ${isDark ? 'via-white/10' : 'via-white/70'}`} />
                    </div>
                    <div className={`relative h-10 w-full overflow-hidden rounded-md ${isDark ? 'bg-gray-700/90' : 'bg-gray-200/90'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-r from-transparent to-transparent animate-shimmer-slide ${isDark ? 'via-white/15' : 'via-white/80'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Region</label>
                  <select
                    name="region"
                    value={formValues.region}
                    onChange={handleInputChange}
                    onFocus={() => { void ensureFullRegionsLoaded(); }}
                    className={selectClass}
                    disabled={isLoading || isLoadingRegions}
                  >
                    <option value="">
                      {isLoadingRegions ? 'Loading regions...' : 'Select Region'}
                    </option>
                    {regions.map((region) => (
                      <option key={region.region_code} value={region.region_description}>
                        {region.region_description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Province</label>
                  {availableProvinces.length > 0 ? (
                    <select
                      name="province"
                      value={formValues.province}
                      onChange={handleInputChange}
                      className={selectClass}
                      disabled={isLoading || isLoadingProvinces || !formValues.region}
                    >
                      <option value="">
                        {isLoadingProvinces ? 'Loading provinces...' : 'Select Province'}
                      </option>
                      {availableProvinces.map((province) => (
                        <option key={province.province_code || province.province_name} value={province.province_name}>
                          {province.province_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="province"
                      value={formValues.province}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter province"
                      disabled={isLoading || isLoadingProvinces || !formValues.region}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Municipality</label>
                  <select
                    name="municipality"
                    value={formValues.municipality}
                    onChange={handleInputChange}
                    className={selectClass}
                    disabled={isLoading || isLoadingCities || !formValues.province}
                  >
                    <option value="">
                      {isLoadingCities
                        ? 'Loading municipalities...'
                        : cities.length === 0 && formValues.province
                          ? 'No municipalities available'
                          : 'Select Municipality'}
                    </option>
                    {cities.map((city) => (
                      <option key={city.city_code} value={city.city_name}>
                        {city.city_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Barangay{' '}
                    {barangays.length > 0 && <span className={`text-xs ${ap.faint}`}>({barangays.length} available)</span>}
                  </label>
                  {isLoadingBarangays ? (
                    <div className={`w-full px-3 py-2 border rounded-md text-sm ${ap.input} ${ap.muted}`}>
                      Loading barangays...
                    </div>
                  ) : barangays.length > 0 ? (
                    <div className="space-y-1">
                      <input
                        type="text"
                        list="update-barangay-list"
                        name="barangay"
                        value={formValues.barangay}
                        onChange={handleInputChange}
                        onInput={(e) => {
                          setFormValues((prev) => ({ ...prev, barangay: (e.target as HTMLInputElement).value }));
                        }}
                        placeholder={barangays.length > 1000 ? 'Type to search barangay...' : 'Select or type barangay...'}
                        className={inputClass}
                        disabled={isLoading || !formValues.municipality}
                        autoComplete="off"
                      />
                      <datalist id="update-barangay-list">
                        {barangays.slice(0, 5000).map((barangay) => (
                          <option key={barangay.id} value={barangay.barangay_name} />
                        ))}
                      </datalist>
                      {barangays.length > 1000 && (
                        <p className={`text-xs ${ap.faint}`}>
                          Large dataset detected. Type to search among {barangays.length.toLocaleString()} barangays.
                        </p>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      name="barangay"
                      value={formValues.barangay}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter barangay manually"
                      disabled={isLoading || !formValues.municipality}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${ap.headerBorder}`}>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${md.cancelBtn}`}
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={
              isLoading ||
              isResolvingLocation ||
              !formValues.name.trim() ||
              mixedLocations
            }
            className={`px-4 py-2 text-sm font-medium rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${ap.primaryBtn}`}
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>
              {isLoading
                ? 'Updating...'
                : mixedLocations
                  ? 'Cannot update — mixed locations'
                  : 'Update Boundary'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
